import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import {
  getPageInfo,
  getPersonalCharactersDetails,
  getUserWclGuilds,
  getUserTeamAccess,
  getUserTeamPreferences,
} from '../api/user'
import { getRealms } from '../api/realm'
import {
  getGuildDetails,
  getGuildDetailsByPath,
  getGuildMembers,
  getGuildNewsItem,
  getGuildNewsItems,
  getGuildOverviewData,
  getGuildTeamData,
  getGuildUsers,
} from '../api/guild'
import { getNeighborhoodOccupants } from '../api/housing'
import { getGlobalNewsItem, getGlobalNewsItems } from '../api/globalNews'
import {
  getAccessRestrictions,
  getAuditData,
  getCharacterWishlist,
  getDiscordReferences,
  getNewsItem,
  getNewsItems,
  getTeamDetails,
  getTeamDetailsByPath,
  getTeamDroptimizerConfigurations,
  EventStatisticsQuery,
  getTeamEventStatistics,
  getTeamLootHistory,
  getTeamRefreshStatus,
  getTeamTrackingRules,
  getTeamUsers,
  getTeamWishlist,
  getSeasonTrinkets,
  LootHistoryUrlOptions,
} from '../api/team'
import {
  getRaidPlan,
  getRaidPlans,
  getRaidPlansIndex,
  getRaidPlanTopExecutionKills,
  RaidPlanOwner,
  UserRaidPlansIndex,
} from '../api/raid_plans'
import { getVisualPlan, getVisualPlans } from '../api/visual_plans'
import { getPlanVersion, getPlanVersions, PlanKind } from '../api/plan_versions'
import { getDefaultSetups } from '../api/default_setups'
import {
  EventActivitiesPlanningInfo,
  EventChangelogEntry,
  getEventRaidReports,
  getEventActivityPlanningKey,
  getTeamEventRaidReportsMap,
  getTeamCalendarEvents,
  getUserListEvents,
  getTeamListEvents,
  getEventActivitiesPlanningInfo,
  getTeamOverviewEvents,
  getTeamEventChangelog,
  getTeamEvent,
  TeamEventRaidReportsMapResponse,
  TeamListEventsResponse,
  TeamOverviewEventsResponse,
} from '../api/event'
import {
  getDiscordChannelDetails,
  getNotifications,
  getOptions,
  getTemplateBlacklists,
  getTemplates,
} from '../api/notification'
import { getOverviewData } from '../api/team'
import {
  getSupportArticle,
  getSupportContent,
  getSupportSectionIntro,
} from '../api/support'
import { getInstances } from '../api/instance'
import { getCharacterDetails, getCharacterProgress } from '../api/character'
import Difficulty from 'app/frontend/types/custom/Difficulty'
import {
  getTeamApplicationDetails,
  getApplicationMessages,
  getTeamApplications,
  getUserApplications,
  getUserApplicationDetails,
  getUserApplicationSummary,
} from '../api/application'
import useReferenceStore from '../state/useReferenceStore'
import useBasePath from '../hooks/useBasePath'
import usePrefetchedStore from '../state/usePrefetchedStore'
import { useCustomDomain } from '../hooks/useCustomDomain'

export const usePageInfo = () => {
  return useQuery({
    queryKey: ['pageInfo'],
    queryFn: getPageInfo,
    staleTime: 1000 * 60 * 15,
    initialData: window.page_info,
  })
}

export const useUserWclGuilds = (enabled: boolean) => {
  return useQuery({
    queryKey: ['userWclGuilds'],
    queryFn: getUserWclGuilds,
    enabled,
    staleTime: 1000 * 60 * 5,
  })
}

export const useRealms = () => {
  return useQuery({
    queryKey: ['realms'],
    queryFn: getRealms,
    staleTime: 1000 * 60 * 60,
  })
}

export const useInstances = () => {
  return useQuery({
    queryKey: ['instances'],
    queryFn: getInstances,
    staleTime: 1000 * 60 * 60,
  })
}

export const useNotificationTemplateOptions = () => {
  return useQuery({
    queryKey: ['notificationTemplateOptions'],
    queryFn: getOptions,
    staleTime: Infinity,
  })
}

export const useNotificationTemplates = (teamId: number) => {
  return useQuery({
    queryKey: ['notificationTemplates', { id: teamId }],
    queryFn: () => getTemplates(teamId),
    staleTime: 1000 * 60 * 60,
  })
}

export const useNotificationTemplateBlacklists = () => {
  return useQuery({
    queryKey: ['notificationTemplateBlacklists'],
    queryFn: () => getTemplateBlacklists(),
    staleTime: 1000 * 60 * 60,
  })
}

export const useUserTeamPreferences = (enabled = true) => {
  return useQuery({
    queryKey: ['userTeamPreferences'],
    queryFn: () => getUserTeamPreferences(),
    staleTime: 1000 * 60 * 60,
    enabled,
  })
}

export const useUserTeamAccess = (teamIds: number[]) => {
  return useQuery({
    queryKey: ['userTeamAccess', { teamIds }],
    queryFn: () => getUserTeamAccess(teamIds),
    staleTime: 1000 * 60 * 60,
    enabled: teamIds.length > 0,
  })
}

export const useDiscordChannelDetails = (addresses: string[]) => {
  return useQuery({
    queryKey: ['discordChannelDetails', { addresses }],
    queryFn: () => getDiscordChannelDetails(addresses),
    staleTime: 1000 * 60 * 60,
  })
}

export const useNotifications = (teamId: number) => {
  return useQuery({
    queryKey: ['notifications', { id: teamId }],
    queryFn: () => getNotifications(teamId),
    staleTime: 1000 * 60 * 15,
  })
}

export const useUserCharacterDetails = (enabled: boolean) => {
  return useQuery({
    queryKey: ['userCharacterDetails'],
    queryFn: getPersonalCharactersDetails,
    enabled: enabled,
    staleTime: 1000 * 60 * 5,
  })
}

export const useCharacterProgress = (characterId?: number) => {
  return useQuery({
    queryKey: ['characterProgress', characterId],
    queryFn: () => getCharacterProgress(characterId!),
    enabled: !!characterId,
    staleTime: 1000 * 60 * 5,
  })
}

export const useGuildDetails = (guildId?: number) => {
  const { customDomain } = useCustomDomain()
  const { storeReferences } = useReferenceStore()
  const { getData } = usePrefetchedStore()
  const baseGuildPath = useBasePath('guild')

  const queryFn = async () => {
    const response = guildId
      ? await getGuildDetails(guildId)
      : await getGuildDetailsByPath(baseGuildPath as string)

    storeReferences([response, ...response.teamReferences])
    return response
  }

  const initialData =
    customDomain?.guild ?? getData<wowaudit.Guild>(baseGuildPath)

  return useQuery({
    queryKey: ['guild', { path: customDomain?.guild ? '' : baseGuildPath }],
    queryFn,
    staleTime: 1000 * 60 * 60,
    initialData,
  })
}

export const useTeamDetails = (teamId?: number) => {
  const { customDomain } = useCustomDomain()
  const { storeReferences } = useReferenceStore()
  const { getData } = usePrefetchedStore()
  const baseTeamPath = useBasePath('team')

  const queryFn = async () => {
    const response = teamId
      ? await getTeamDetails(teamId)
      : await getTeamDetailsByPath(baseTeamPath as string)

    storeReferences([
      response.ownerReference,
      ...(response.members?.map((member) => member.characterReference) ?? []),
    ])
    return response
  }

  const initialData = customDomain?.team ?? getData<wowaudit.Team>(baseTeamPath)

  return useQuery({
    queryKey: ['team', { path: customDomain?.team ? '' : baseTeamPath }],
    queryFn,
    enabled: !!teamId || !!baseTeamPath,
    staleTime: 1000 * 60 * 60,
    initialData,
  })
}

export const useTeamDetailsById = (teamId?: number) => {
  const { storeReferences } = useReferenceStore()

  return useQuery({
    queryKey: ['team', { id: teamId }],
    queryFn: async () => {
      const response = await getTeamDetails(teamId as number)
      storeReferences([
        response.ownerReference,
        ...(response.members?.map((member) => member.characterReference) ?? []),
      ])
      return response
    },
    enabled: !!teamId,
    staleTime: 1000 * 60 * 60,
  })
}

export const useDiscordReferences = (teamId?: number) => {
  return useQuery({
    queryKey: ['discordReferences', { id: teamId }],
    queryFn: () => (teamId ? getDiscordReferences(teamId) : undefined),
    staleTime: 1000 * 60 * 60,
    enabled: !!teamId,
  })
}

export const useAccessRestrictions = (teamId?: number) => {
  return useQuery({
    queryKey: ['accessRestrictions', { id: teamId }],
    queryFn: () => (teamId ? getAccessRestrictions(teamId) : undefined),
    staleTime: 1000 * 60 * 60,
    enabled: !!teamId,
  })
}

export const useCharacterDetails = (characterId?: number) => {
  return useQuery({
    queryKey: ['characterDetails', { characterId }],
    queryFn: () =>
      characterId ? getCharacterDetails([characterId]) : undefined,
    staleTime: 1000 * 60 * 15,
    enabled: !!characterId,
  })
}

export const useTeamCharacterDetails = (
  teamId?: number,
  ids?: number[],
  key?: string,
) => {
  return useQuery({
    queryKey: ['teamCharacterDetails', { id: teamId, key: key ?? '' }],
    queryFn: () => (teamId && ids ? getCharacterDetails(ids) : undefined),
    staleTime: 1000 * 60 * 15,
    enabled: !!teamId && !!ids,
  })
}

export const useTeamTrackingRules = (teamId?: number) => {
  return useQuery({
    queryKey: ['teamTrackingRules', { id: teamId }],
    queryFn: () => (teamId ? getTeamTrackingRules(teamId) : undefined),
    staleTime: 1000 * 60 * 5,
    enabled: !!teamId,
  })
}

export const useTeamDroptimizerConfigurations = (teamId?: number) => {
  return useQuery({
    queryKey: ['teamDroptimizerConfigurations', { id: teamId }],
    queryFn: () =>
      teamId ? getTeamDroptimizerConfigurations(teamId) : undefined,
    staleTime: 1000 * 60 * 5,
    enabled: !!teamId,
  })
}

export const useGuildMembers = (guildId: number | undefined) => {
  return useQuery({
    queryKey: ['guildMembers', { id: guildId }],
    queryFn: () => (guildId ? getGuildMembers(guildId) : undefined),
    enabled: !!guildId,
    staleTime: 1000 * 60 * 60,
  })
}

export const useGlobalNewsItems = () => {
  return useQuery({
    queryKey: ['globalNewsItems'],
    queryFn: () => getGlobalNewsItems(),
    staleTime: 1000 * 60 * 5,
  })
}

export const useGlobalNewsItem = (id?: number) => {
  return useQuery({
    queryKey: ['globalNewsItem', { id }],
    queryFn: () => (id ? getGlobalNewsItem(id) : undefined),
    staleTime: 1000 * 60 * 5,
    enabled: !!id,
  })
}

export const useGuildNewsItems = (guildId?: number) => {
  return useQuery({
    queryKey: ['guildNewsItems', { guildId }],
    queryFn: () => (guildId ? getGuildNewsItems(guildId) : undefined),
    staleTime: 1000 * 60 * 5,
    enabled: !!guildId,
  })
}

export const useGuildNewsItem = (guildId?: number, id?: number) => {
  return useQuery({
    queryKey: ['guildNewsItem', { guildId, id }],
    queryFn: () => (guildId && id ? getGuildNewsItem(guildId, id) : undefined),
    staleTime: 1000 * 60 * 5,
    enabled: !!guildId && !!id,
  })
}

export const useGuildOverviewData = (guildId?: number) => {
  return useQuery({
    queryKey: ['guildOverviewData', { id: guildId }],
    queryFn: () => (guildId ? getGuildOverviewData(guildId) : undefined),
    staleTime: 1000 * 60 * 5,
    enabled: !!guildId,
  })
}

export const useGuildTeamData = (guildId?: number) => {
  return useQuery({
    queryKey: ['guildTeamData', { id: guildId }],
    queryFn: () => (guildId ? getGuildTeamData(guildId) : undefined),
    staleTime: 1000 * 60 * 5,
    enabled: !!guildId,
  })
}

export const useGuildUsers = (guildId?: number) => {
  return useQuery({
    queryKey: ['guildUsers', { id: guildId }],
    queryFn: () => (guildId ? getGuildUsers(guildId) : undefined),
    staleTime: 1000 * 60 * 60,
    enabled: !!guildId,
  })
}

export const useNeighborhoodOccupants = (guildId: number | undefined) => {
  return useQuery({
    queryKey: ['neighborhoodOccupants', { guildId }],
    queryFn: () =>
      guildId ? getNeighborhoodOccupants(guildId) : Promise.resolve(undefined),
    enabled: !!guildId,
    staleTime: 1000 * 60 * 5,
    refetchInterval: 1000 * 60 * 5,
  })
}

export const useTeamUsers = (teamId?: number) => {
  return useQuery({
    queryKey: ['teamUsers', { id: teamId }],
    queryFn: () => (teamId ? getTeamUsers(teamId) : undefined),
    staleTime: 1000 * 60 * 60,
    enabled: !!teamId,
  })
}

export const useUserApplications = (pendingOnly = false) => {
  const {
    data: { user },
  } = usePageInfo()

  return useQuery({
    queryKey: ['userApplications', { pendingOnly }],
    queryFn: async () => {
      const applications = await getUserApplications(pendingOnly)
      return applications
    },
    enabled: !!user,
    staleTime: 1000 * 60 * 15,
  })
}

export const useTeamApplications = (
  teamId?: number,
  pendingOnly = false,
  hasAccess = true,
) => {
  const { storeReferences } = useReferenceStore()

  return useQuery({
    queryKey: ['teamApplications', { id: teamId, pendingOnly }],
    queryFn: async () => {
      if (!teamId) return undefined
      const applications = await getTeamApplications(teamId, pendingOnly)
      storeReferences(
        applications
          .map((application) => application.characterReference)
          .filter((reference) => reference !== undefined),
      )
      return applications
    },
    staleTime: 1000 * 60 * 15,
    enabled: !!teamId && hasAccess,
  })
}

export const useUserApplicationSummary = () => {
  const {
    data: { user },
  } = usePageInfo()

  return useQuery({
    queryKey: ['userApplicationSummary'],
    queryFn: getUserApplicationSummary,
    staleTime: 1000 * 60 * 15,
    initialData: user?.openApplicationSummary,
    enabled: !!user,
  })
}

export const useApplicationDetails = (teamId?: number, id?: number) => {
  const { storeReferences } = useReferenceStore()
  return useQuery({
    queryKey: ['applicationDetails', { teamId, id }],
    queryFn: async () => {
      if (!id) return undefined
      const application = teamId
        ? await getTeamApplicationDetails(teamId, id)
        : await getUserApplicationDetails(id)
      storeReferences(
        application.alts.concat(
          application.characterReference
            ? [application.characterReference]
            : [],
        ),
      )
      return application
    },
    staleTime: 1000 * 60 * 15,
    enabled: !!id,
  })
}

export const useApplicationMessages = (
  open: boolean,
  type: 'messages' | 'notes',
  teamId?: number,
  id?: number,
) => {
  const { data: applicationDetails } = useApplicationDetails(teamId, id)

  return useQuery({
    queryKey: ['applicationMessages', { teamId, id, type }],
    queryFn: () =>
      teamId && id ? getApplicationMessages(teamId, id, type) : undefined,
    refetchInterval: 1000 * 30,
    initialData: applicationDetails?.messages?.filter((m) =>
      type === 'notes' ? m.internal : !m.internal,
    ),
    initialDataUpdatedAt: 0,
    enabled: open && !!teamId && !!id,
  })
}

export const useCharacterWishlist = (
  teamId?: number,
  characterId?: number,
  seasonId?: number,
) => {
  return useQuery({
    queryKey: ['characterWishlist', { teamId, characterId, seasonId }],
    queryFn: () =>
      teamId && characterId && seasonId
        ? getCharacterWishlist(teamId, characterId, seasonId)
        : undefined,
    staleTime: 1000 * 60 * 60,
  })
}

export const useTeamWishlist = (
  teamId: number,
  difficulty: Difficulty,
  seasonId: number,
  configurationId?: number,
  source?: 'raid' | 'dungeon',
) => {
  return useQuery({
    queryKey: [
      'teamWishlist',
      { teamId, difficulty, seasonId, configurationId, source },
    ],
    queryFn: () =>
      teamId && seasonId
        ? getTeamWishlist(teamId, difficulty, seasonId, configurationId, source)
        : undefined,
    staleTime: 1000 * 60 * 15,
    enabled: !!teamId && !!seasonId,
  })
}

export const useSeasonTrinkets = (teamId: number, seasonId: number) => {
  return useQuery({
    queryKey: ['seasonTrinkets', { teamId, seasonId }],
    queryFn: () =>
      teamId && seasonId ? getSeasonTrinkets(teamId, seasonId) : undefined,
    staleTime: 1000 * 60 * 60,
    enabled: !!teamId && !!seasonId,
  })
}

export const useTeamLootHistory = (options: LootHistoryUrlOptions) => {
  return useQuery({
    queryKey: ['teamLootHistory', options],
    queryFn: () => getTeamLootHistory(options),
    staleTime: 1000 * 60 * 15,
  })
}

export const useUserRaidPlansIndex = (
  seasonId?: number,
  difficulty?: Difficulty,
  enabled = true,
) => {
  const owner = { ownerType: 'user' } as const
  return useQuery({
    queryKey: ['userRaidPlans', { seasonId, difficulty }],
    queryFn: async () =>
      (await getRaidPlansIndex(
        owner,
        seasonId,
        difficulty,
      )) as UserRaidPlansIndex,
    enabled: enabled && !!seasonId,
    staleTime: 1000 * 30,
  })
}

export const useRaidPlans = (
  owner?: RaidPlanOwner,
  seasonId?: number,
  enabled = true,
) => {
  return useQuery({
    queryKey: ['raidPlans', { owner, seasonId }],
    queryFn: () => (owner ? getRaidPlans(owner, seasonId) : undefined),
    enabled: enabled && !!owner && !!seasonId,
    staleTime: 1000 * 30,
  })
}

export const useRaidPlan = (
  ownerOrTeamId?: RaidPlanOwner | number,
  planId?: number,
) => {
  const owner =
    typeof ownerOrTeamId === 'number'
      ? ({ ownerType: 'team', ownerId: ownerOrTeamId } as const)
      : ownerOrTeamId
  return useQuery({
    queryKey: ['raidPlan', { owner, planId }],
    queryFn: () => (owner && planId ? getRaidPlan(owner, planId) : undefined),
    enabled: !!owner && !!planId,
    staleTime: 1000 * 30,
  })
}

export const useRaidPlanTopExecutionKills = (
  ownerOrTeamId?: RaidPlanOwner | number,
  encounterId?: number,
  difficulty?: Difficulty,
  enabled = true,
) => {
  const owner =
    typeof ownerOrTeamId === 'number'
      ? ({ ownerType: 'team', ownerId: ownerOrTeamId } as const)
      : ownerOrTeamId
  return useQuery({
    queryKey: ['raidPlanTopExecutionKills', { owner, encounterId, difficulty }],
    queryFn: () =>
      owner && encounterId && difficulty
        ? getRaidPlanTopExecutionKills(owner, encounterId, difficulty)
        : undefined,
    enabled: enabled && !!owner && !!encounterId && !!difficulty,
    staleTime: 1000 * 30,
  })
}

export const useDefaultSetups = (teamId?: number, enabled = true) => {
  return useQuery({
    queryKey: ['defaultSetups', teamId],
    queryFn: () => (teamId ? getDefaultSetups(teamId) : undefined),
    enabled: enabled && !!teamId,
    staleTime: 1000 * 30,
  })
}

export const useVisualPlans = (
  owner?: RaidPlanOwner,
  seasonId?: number,
  difficulty?: Difficulty,
  enabled = true,
) => {
  return useQuery({
    queryKey: ['visualPlans', { owner, seasonId, difficulty }],
    queryFn: () =>
      owner ? getVisualPlans(owner, seasonId, difficulty) : undefined,
    enabled: enabled && !!owner && !!seasonId,
    staleTime: 1000 * 30,
  })
}

export const useEncounterVisualPlans = (
  owner?: RaidPlanOwner,
  encounterId?: number,
  difficulty?: Difficulty,
  enabled = true,
) => {
  return useQuery({
    queryKey: ['visualPlans', { owner, encounterId, difficulty }],
    queryFn: () =>
      owner && encounterId
        ? getVisualPlans(owner, undefined, difficulty, encounterId)
        : undefined,
    enabled: enabled && !!owner && !!encounterId,
    staleTime: 1000 * 30,
  })
}

export const useVisualPlan = (
  ownerOrTeamId?: RaidPlanOwner | number,
  visualId?: number,
) => {
  const owner =
    typeof ownerOrTeamId === 'number'
      ? ({ ownerType: 'team', ownerId: ownerOrTeamId } as const)
      : ownerOrTeamId
  return useQuery({
    queryKey: ['visualPlan', { owner, visualId }],
    queryFn: () =>
      owner && visualId ? getVisualPlan(owner, visualId) : undefined,
    enabled: !!owner && !!visualId,
    staleTime: 1000 * 30,
  })
}

export const usePlanVersions = (
  kind: PlanKind,
  owner?: RaidPlanOwner,
  planId?: number,
  enabled = true,
) => {
  return useQuery({
    queryKey: ['planVersions', { kind, owner, planId }],
    queryFn: () =>
      owner && planId ? getPlanVersions(kind, owner, planId) : undefined,
    enabled: enabled && !!owner && !!planId,
    staleTime: 0,
  })
}

export const usePlanVersion = (
  kind: PlanKind,
  owner?: RaidPlanOwner,
  planId?: number,
  versionId?: number,
) => {
  return useQuery({
    queryKey: ['planVersion', { kind, owner, planId, versionId }],
    queryFn: () =>
      owner && planId && versionId
        ? getPlanVersion(kind, owner, planId, versionId)
        : undefined,
    enabled: !!owner && !!planId && !!versionId,
    staleTime: Infinity,
    gcTime: Infinity,
  })
}

export const useTeamAuditData = (teamId?: number, prefetchReady = true) => {
  return useQuery({
    queryKey: ['teamAuditData', { id: teamId }],
    queryFn: () => (teamId ? getAuditData(teamId) : undefined),
    staleTime: 1000 * 60 * 15,
    refetchOnMount: 'always',
    enabled: !!teamId && prefetchReady,
  })
}

export const useTeamEventStatistics = (
  teamId?: number,
  query?: EventStatisticsQuery,
) => {
  const isRangeQuery = !!query?.from && !!query?.to
  const isSeasonQuery = !!query?.seasonId

  return useQuery({
    queryKey: ['teamEventStatistics', { teamId, query }],
    queryFn: () =>
      teamId && query ? getTeamEventStatistics(teamId, query) : undefined,
    staleTime: 1000 * 60 * 5,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    enabled: !!teamId && (isRangeQuery || isSeasonQuery),
  })
}

export const useNewsItems = (teamId?: number) => {
  return useQuery({
    queryKey: ['newsItems', { teamId }],
    queryFn: () => (teamId ? getNewsItems(teamId) : undefined),
    staleTime: 1000 * 60 * 5,
    enabled: !!teamId,
  })
}

export const useNewsItem = (teamId?: number, id?: number) => {
  return useQuery({
    queryKey: ['newsItem', { teamId, id }],
    queryFn: () => (teamId && id ? getNewsItem(teamId, id) : undefined),
    staleTime: 1000 * 60 * 5,
    enabled: !!teamId && !!id,
  })
}

export const useTeamRefreshStatus = (teamId?: number) => {
  return useQuery({
    queryKey: ['teamRefreshStatus', { id: teamId }],
    queryFn: () => (teamId ? getTeamRefreshStatus(teamId) : undefined),
    staleTime: 1000 * 10,
    refetchInterval: 1000 * 10,
    enabled: !!teamId,
  })
}

export const useTeamOverviewEvents = (
  teamId?: number,
  options?: {
    limit?: number
    initialCursor?: number
    initialDirection?: 'before' | 'after'
    resetVersion?: number
  },
) => {
  const { storeReferences } = useReferenceStore()
  const limit = options?.limit ?? 15

  return useInfiniteQuery<TeamOverviewEventsResponse>({
    queryKey: [
      'teamEvents',
      {
        id: teamId,
        type: 'overview',
        limit: limit,
        initialCursor: options?.initialCursor,
        initialDirection: options?.initialDirection,
        resetVersion: options?.resetVersion,
      },
    ],
    queryFn: async ({ pageParam }) => {
      if (!teamId) {
        return {
          events: [],
          hasNextPage: false,
          hasPrevPage: false,
        }
      }
      const page = pageParam as
        | { cursor?: number; dir?: 'before' | 'after' }
        | undefined
      const response = await getTeamOverviewEvents(teamId, {
        cursor: page?.cursor,
        dir: page?.dir,
        limit: limit,
      })
      storeReferences(response.events)
      return response
    },
    initialPageParam: {
      cursor: options?.initialCursor,
      dir: options?.initialDirection,
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage.hasNextPage || !lastPage.nextCursor) return undefined
      return { cursor: lastPage.nextCursor, dir: 'after' as const }
    },
    getPreviousPageParam: (firstPage) => {
      if (!firstPage.hasPrevPage || !firstPage.prevCursor) return undefined
      return { cursor: firstPage.prevCursor, dir: 'before' as const }
    },
    maxPages: 1,
    staleTime: 0,
    gcTime: 0,
    placeholderData: (previousData) => previousData,
    refetchOnMount: 'always',
    enabled: !!teamId,
  })
}

export const useTeamCalendarEvents = (teamId?: number, month?: string) => {
  const { storeReferences } = useReferenceStore()

  return useQuery({
    queryKey: ['teamEvents', { id: teamId, type: 'calendar', month }],
    queryFn: async () => {
      if (!teamId || !month) return []
      const events = await getTeamCalendarEvents(teamId, { month })
      storeReferences(events)
      return events
    },
    enabled: !!teamId && !!month,
  })
}

export const useTeamListEvents = (
  teamId?: number,
  page = 1,
  tab: 'upcoming' | 'past' = 'upcoming',
) => {
  const { storeReferences } = useReferenceStore()

  return useQuery<TeamListEventsResponse>({
    queryKey: ['teamEvents', { id: teamId, type: 'list', page, tab }],
    queryFn: async () => {
      if (!teamId) {
        return {
          events: [],
          page: 1,
          totalPages: 1,
          totalCount: 0,
        }
      }
      const response = await getTeamListEvents(teamId, { page, tab })
      storeReferences(response.events)
      return response
    },
    placeholderData: keepPreviousData,
    enabled: !!teamId,
  })
}

export const useUserListEvents = (
  page = 1,
  tab: 'upcoming' | 'past' = 'upcoming',
) => {
  const { storeReferences } = useReferenceStore()
  const {
    data: { user },
  } = usePageInfo()

  return useQuery<TeamListEventsResponse>({
    queryKey: ['userEvents', { type: 'list', page, tab }],
    queryFn: async () => {
      const response = await getUserListEvents({ page, tab })
      storeReferences(response.events)
      return response
    },
    placeholderData: keepPreviousData,
    enabled: !!user,
  })
}

export const useTeamEvent = (
  teamId?: number,
  eventId?: number,
  initialData?: wowaudit.Event | undefined,
) => {
  return useQuery({
    queryKey: ['teamEvent', { teamId, eventId }],
    queryFn: () =>
      teamId && eventId ? getTeamEvent(teamId, eventId) : undefined,
    enabled: !!teamId && !!eventId,
    staleTime: 0,
    initialData,
  })
}

export const useTeamEventChangelog = (
  open: boolean,
  teamId?: number,
  eventId?: number,
) => {
  return useQuery<{ changelog: EventChangelogEntry[] }>({
    queryKey: ['teamEventChangelog', { teamId, eventId }],
    queryFn: () => getTeamEventChangelog(teamId as number, eventId as number),
    enabled: open && !!teamId && !!eventId,
  })
}

export const useEventRaidReports = (
  teamId?: number,
  eventId?: number,
  enabled = false,
) => {
  return useQuery<wowaudit.RaidReport[]>({
    queryKey: ['eventRaidReports', { teamId, eventId }],
    queryFn: () => getEventRaidReports(teamId as number, eventId as number),
    enabled: enabled && !!teamId && !!eventId,
    staleTime: 1000 * 60,
  })
}

export const useTeamEventRaidReportsMap = (teamId?: number, enabled = true) => {
  return useQuery<TeamEventRaidReportsMapResponse>({
    queryKey: ['teamEventRaidReportsMap', { teamId }],
    queryFn: () => getTeamEventRaidReportsMap(teamId as number),
    enabled: enabled && !!teamId,
    staleTime: 1000 * 60,
  })
}

export const useEventActivitiesPlanningInfo = (
  teamId?: number,
  eventId?: number,
  activities: wowaudit.EventActivity[] = [],
  enabled = false,
) => {
  const queryClient = useQueryClient()
  const uniqueActivityKeys = Array.from(
    new Set(
      activities.map((activity) => getEventActivityPlanningKey(activity)),
    ),
  )
  const cachedActivities = uniqueActivityKeys.reduce<
    EventActivitiesPlanningInfo['activities'] | null
  >((acc, activityKey) => {
    if (!teamId || !acc) return null
    const cached = queryClient.getQueryData<
      EventActivitiesPlanningInfo['activities'][string]
    >(['eventActivityPlanningInfo', { teamId, activityKey }])
    if (!cached) return null
    acc[activityKey] = cached
    return acc
  }, {})
  const hasFullCache = uniqueActivityKeys.length > 0 && !!cachedActivities

  return useQuery({
    queryKey: [
      'eventActivitiesPlanningInfo',
      { teamId, eventId, uniqueActivityKeys },
    ],
    queryFn: async () => {
      if (!teamId || !eventId) return undefined
      const response = await getEventActivitiesPlanningInfo(teamId, eventId)
      Object.entries(response.activities).forEach(
        ([activityKey, planningInfo]) => {
          queryClient.setQueryData(
            ['eventActivityPlanningInfo', { teamId, activityKey }],
            planningInfo,
          )
        },
      )
      return response
    },
    initialData: cachedActivities
      ? { activities: cachedActivities }
      : undefined,
    enabled: enabled && !!teamId && !!eventId && !hasFullCache,
    staleTime: 1000 * 60 * 5,
  })
}

export const useOverviewData = (teamId?: number) => {
  const queryClient = useQueryClient()

  return useQuery({
    queryKey: ['overviewData', { id: teamId }],
    queryFn: async () => {
      if (!teamId) return undefined
      const data = await getOverviewData(teamId)

      if (data?.auditData) {
        queryClient.setQueryData(
          ['teamAuditData', { id: teamId }],
          data.auditData,
        )
      }

      return data
    },
    staleTime: 1000 * 60 * 5,
    refetchOnMount: 'always',
    enabled: !!teamId,
  })
}

export const useSupportContent = () => {
  return useQuery({
    queryKey: ['supportContent'],
    queryFn: getSupportContent,
    staleTime: 1000 * 60 * 5,
  })
}

export const useSupportArticle = (section?: string, slug?: string) => {
  return useQuery({
    queryKey: ['supportArticle', { section, slug }],
    queryFn: () =>
      section && slug ? getSupportArticle(section, slug) : undefined,
    staleTime: 1000 * 60 * 5,
    enabled: !!section && !!slug,
  })
}

export const useSupportSectionIntro = (section?: string) => {
  return useQuery({
    queryKey: ['supportSectionIntro', { section }],
    queryFn: () => (section ? getSupportSectionIntro(section) : undefined),
    staleTime: 1000 * 60 * 5,
    enabled: !!section,
  })
}
